import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Shield, MessageCircle, Smartphone, Heart, BadgeCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Sobre nosotros · habita.co"
        description="Habita es una plataforma inmobiliaria moderna creada para transformar la manera en que las personas descubren, publican y conectan con propiedades en Colombia."
      />
      <Header />

      {/* HERO */}
      <section className="bg-habita-cream py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-habita-mint text-habita rounded-full text-xs font-semibold tracking-wide">
            <Sparkles className="w-3.5 h-3.5" /> SOBRE HABITA · BETA EN COLOMBIA
          </span>
          <h1 className="font-serif text-5xl lg:text-7xl font-bold text-habita-dark mt-5 leading-[1.05]">
            Una nueva forma <br />
            de <span className="italic text-amber-600">vivir lo inmobiliario</span>.
          </h1>
          <p className="text-gray-600 mt-6 text-lg max-w-2xl mx-auto leading-relaxed">
            Plataforma inmobiliaria moderna creada para transformar la manera en que las personas descubren, publican y conectan con propiedades en Colombia.
          </p>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-4xl mx-auto px-4 lg:px-8 py-20">
        <div className="prose prose-lg max-w-none">
          <p className="text-lg lg:text-xl leading-relaxed text-gray-700">
            Nacimos con la idea de construir una experiencia más <strong className="text-habita-dark">simple, transparente y humana</strong> dentro del mercado inmobiliario, combinando tecnología, diseño y herramientas digitales que realmente faciliten el proceso tanto para usuarios como para agentes e inmobiliarias.
          </p>

          <p className="text-lg lg:text-xl leading-relaxed text-gray-700 mt-7">
            En un sector donde muchas plataformas se sienten complejas, desactualizadas o saturadas, Habita busca ofrecer una experiencia más <strong className="text-habita-dark">rápida, visual y enfocada en la confianza</strong>. Por eso trabajamos con propiedades verificadas, contacto directo vía WhatsApp y una plataforma diseñada principalmente para dispositivos móviles y nuevas generaciones digitales.
          </p>

          <p className="text-lg lg:text-xl leading-relaxed text-gray-700 mt-7">
            Nuestro objetivo es ayudar a las personas a encontrar espacios donde realmente puedan construir su vida: desde apartamentos y casas hasta oportunidades de inversión y proyectos inmobiliarios.
          </p>
        </div>
      </section>

      {/* PILARES */}
      <section className="bg-habita-dark text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Lo que nos diferencia</span>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold mt-3 text-white leading-tight">
              Construido alrededor de <span className="italic text-amber-400">cuatro principios</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: BadgeCheck, title: 'Propiedades verificadas', desc: 'Cada listado pasa por revisión humana antes de salir al público.' },
              { icon: MessageCircle, title: 'Contacto WhatsApp', desc: 'Conexión directa entre interesados y agentes, sin formularios eternos.' },
              { icon: Smartphone, title: 'Mobile-first', desc: 'Diseñado primero para celular. Rápido, fluido y pensado para nuevas generaciones.' },
              { icon: Heart, title: 'Hecho con cariño', desc: 'Cada pixel ha sido pensado para sentirse premium, cercano y confiable.' },
            ].map((p, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-amber-400/40 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-amber-400/15 text-amber-400 flex items-center justify-center">
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-xl font-bold mt-4 text-white">{p.title}</h3>
                <p className="text-sm text-white/70 mt-2 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BETA */}
      <section className="max-w-4xl mx-auto px-4 lg:px-8 py-20 text-center">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold tracking-wide">
          <Shield className="w-3.5 h-3.5" /> FASE BETA
        </span>
        <h2 className="font-serif text-3xl lg:text-5xl font-bold text-habita-dark mt-5 leading-tight">
          Creciendo junto a <span className="italic text-habita">una comunidad</span>.
        </h2>
        <p className="text-gray-600 mt-5 text-lg leading-relaxed max-w-2xl mx-auto">
          Actualmente Habita se encuentra en fase beta, creciendo junto a una comunidad de usuarios, agentes y aliados que creen en una nueva forma de vivir la experiencia inmobiliaria en Colombia.
        </p>
        <p className="text-habita-dark mt-7 text-xl font-serif italic max-w-2xl mx-auto leading-relaxed">
          Más que una plataforma, queremos construir una marca moderna, cercana y confiable para quienes buscan su próximo hogar.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 lg:px-8 pb-24">
        <div className="bg-habita-cream rounded-3xl p-10 lg:p-14 text-center">
          <h3 className="font-serif text-3xl lg:text-4xl font-bold text-habita-dark">¿Quieres ser parte de la beta?</h3>
          <p className="text-gray-600 mt-3 max-w-xl mx-auto">Empieza ahora — buscar, publicar y conectar con propiedades verificadas en Colombia. Es completamente gratis durante la beta.</p>
          <div className="mt-7 flex gap-3 justify-center flex-wrap">
            <Link to="/buscar" className="btn-habita inline-flex items-center gap-2 px-7 h-12 rounded-full font-semibold text-sm" data-testid="about-cta-search">
              Explorar propiedades <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contacto" className="inline-flex items-center gap-2 px-7 h-12 rounded-full font-semibold text-sm border border-habita text-habita hover:bg-white transition-colors" data-testid="about-cta-contact">
              Contáctanos
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
